import { Controller } from "@hotwired/stimulus"

/**
 * Event Check-In Highlight Controller
 *
 * Attached to the most recent row in the kiosk's recent-check-ins list
 * whenever a fresh (non-duplicate) check-in is persisted. Strips the
 * `bg-success/20 event-check-in-row-fresh` highlight classes after a short
 * delay so staff see a clear "just added!" flash without the highlight
 * persisting on subsequent turbo-stream refreshes.
 *
 * The element already animates via `transition-colors duration-1000`; we
 * just remove the class so Tailwind's transition fades the background back
 * to transparent.
 *
 * Note: the kiosk's persistent confirmation banner with the Undo button +
 * 5-second countdown is owned by `kiosk_flash_controller` (rendered into
 * `#kiosk_flash_slot` by `check_in.turbo_stream.erb`). This controller is
 * intentionally small — its only job is the row-level highlight fade.
 */
export default class extends Controller<HTMLElement> {
  static values = {
    delayMs: { type: Number, default: 2000 },
  }

  declare readonly delayMsValue: number

  private timer: number | null = null

  connect() {
    this.timer = window.setTimeout(() => {
      this.element.classList.remove("bg-success/20", "event-check-in-row-fresh")
    }, this.delayMsValue)
  }

  disconnect() {
    if (this.timer !== null) {
      window.clearTimeout(this.timer)
      this.timer = null
    }
  }
}
